import {Box, ButtonBase, Typography} from '@material-ui/core';
import useInterval from '@use-it/interval';
import VerificationCodeInput, {
  VerificationCodeInputProps,
} from 'components/VerificationCodeInput';
import {makeTrackable} from 'modules/ga';
import React, {FunctionComponent, useCallback, useState} from 'react';
import {useIntl} from 'react-intl';

const DEFAULT_TIMEOUT_IN_MS = 1000;
const COUNTDOWN_MAX_VALUE_IN_SECS = 30;
const COUNTDOWN_INTERVAL_IN_SECS = 1;

const TrackableButtonBase = makeTrackable(ButtonBase);

interface VerificationCodeInputWithCountdownProps
  extends VerificationCodeInputProps {
  // is used as a GA event category
  id: string;
  onDidNotReceiveClick: () => void;
  countdownMaxValue?: number;
}

// @fixme: only supports values less then 60
const formatCountdownValue = (value: number) => {
  const normalizedValue = value % 60;

  return `00:${(normalizedValue < 10 ? '0' : '') + normalizedValue}`;
};

const VerificationCodeInputWithCountdown: FunctionComponent<
  VerificationCodeInputWithCountdownProps
> = ({
  id,
  onDidNotReceiveClick,
  countdownMaxValue = COUNTDOWN_MAX_VALUE_IN_SECS,
  ...inputProps
}: VerificationCodeInputWithCountdownProps) => {
  const {formatMessage: fm} = useIntl();
  const [countdownValue, setCountdownValue] = useState(countdownMaxValue);

  const delay = countdownValue ? DEFAULT_TIMEOUT_IN_MS : null;

  useInterval(() => {
    setCountdownValue(countdownValue - COUNTDOWN_INTERVAL_IN_SECS);
  }, delay);

  const handleDidNotReceiveClick = useCallback(() => {
    setCountdownValue(countdownMaxValue);
    onDidNotReceiveClick();
  }, [onDidNotReceiveClick, countdownMaxValue]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={1}>
        <Typography>
          {fm({
            id: 'verification_code_input.code_from_sms',
          })}
        </Typography>
        {countdownValue ? (
          <Typography color="textSecondary">
            {formatCountdownValue(countdownValue)}
          </Typography>
        ) : (
          <TrackableButtonBase
            gaEvent={{
              category: id,
              action: 'did_not_receive_code',
            }}
            onClick={handleDidNotReceiveClick}
          >
            <Typography color="textSecondary">
              {fm({
                id: 'verification_code_input.did_not_receive',
              })}
            </Typography>
          </TrackableButtonBase>
        )}
      </Box>
      <VerificationCodeInput {...inputProps} />
    </Box>
  );
};

export default VerificationCodeInputWithCountdown;
